import type { ValidEndpoints } from '@/api/clients/backend-client'
import { buildFetchHeaders, buildUrl, fetchCollection, updateItem } from './helper-functions'

// Current auth process
// - auth with keycloak
// - check if user exists in db
// - create user if does not exist
// - fetch user data
// - fetch everything else

export type User_Expected_API_DTO = {
  id: string
  first_name: string
  last_name: string
  email: string
  display_name: string
  avatar_url: string | null
  fleet_id: string | null // should only be null if yet to create a fleet, invited users should always have this populated
  push_id: string | null
  external_id: string
}

export type User_Expanded_DTO = User_Expected_API_DTO & {
  current_user?: boolean
}

async function fetch_current_users_id(): Promise<string | undefined> {
  const endpoint: ValidEndpoints = 'rpc/current_user_id'
  try {
    const response = await fetch(buildUrl(endpoint), {
      method: 'GET',
      headers: buildFetchHeaders(),
    })
    const data = await response.json()
    return data
  } catch (error) {
    console.error(error)
    return
  }
}

async function create_user_from_token(): Promise<User_Expected_API_DTO | undefined> {
  const endpoint: ValidEndpoints = 'rpc/create_user'
  try {
    const response = await fetch(buildUrl(endpoint), {
      method: 'POST',
      headers: buildFetchHeaders(),
    })
    const data = await response.json()
    return data
  } catch (error) {
    console.error(error)
    return
  }
}

async function fetch_users(): Promise<User_Expected_API_DTO[] | undefined> {
  const endpoint: ValidEndpoints = 'users'
  return fetchCollection<User_Expected_API_DTO>(endpoint)
}

export async function validate_and_fetch_users(): Promise<User_Expanded_DTO[] | undefined> {
  let current_user_UUID: string | null = null

  // check current logged in user exists.
  const user_id_res = await fetch_current_users_id()
  if (user_id_res) {
    current_user_UUID = user_id_res
  } else {
    // create user if no user object exists for the current logged in user.
    const create_res = await create_user_from_token()
    if (create_res) {
      current_user_UUID = create_res.id
    } else return undefined // first bail out, if user can not be found by token and not created by token.
  }
  // fetch user from tokens fleet users
  const fleet_users = await fetch_users()
  if (fleet_users && fleet_users.length) {
    const processed_users: User_Expanded_DTO[] = fleet_users.map((user) => ({
      ...user,
      current_user: !!(current_user_UUID && user.id === current_user_UUID),
    }))
    return processed_users
  } else {
    return undefined // second bail out, if no fleet users could be fetched with users token
  }
}

export async function updateUser({
  data_to_update,
  user_id,
}: {
  data_to_update: Partial<User_Expected_API_DTO>
  user_id: string
}): Promise<User_Expected_API_DTO | undefined> {
  // check if anything to update

  // Guard clause verses no data to updated
  if (!Object.keys(data_to_update).length) throw new Error('No data to update')

  // ensure no fields that should not be overwritten are present
  const allowed_keys = ['first_name', 'last_name', 'display_name', 'avatar_url']
  const processed_data_to_update = Object.fromEntries(
    Object.entries(data_to_update).filter(([key]) => allowed_keys.includes(key)),
  )

  // Guard clause verses no data to updated after striping out data that should never be altered by the client
  if (!Object.keys(processed_data_to_update).length) throw new Error('No data to update')

  // update user
  const endpoint: ValidEndpoints = 'users'
  const res = await updateItem<User_Expected_API_DTO>({
    endpoint,
    query: { id: user_id },
    body: processed_data_to_update,
  })
  // return updated user
  return res
}
